/**
 * @typedef {'pb'|'sp'} TPanelKey
 * @typedef {string} TFontName
 * @typedef {string} TFontLabel
 * @typedef {{
 *     name: string,
 *     authors: string[],
 * }} TBaseTemplate
 * @typedef {TBaseTemplate & {
 *     left: string,
 *     right: string,
 *     progress: string,
 *     rightProgress: string,
 *     empty: string,
 * }} TProgressTemplate
 * @typedef {TBaseTemplate & {
 *     frames: string[],
 * }} TSpinnerTemplate
 * @typedef {{
 *     fonts: Record<TFontName, null|TFontLabel>,
 *     templates: {
 *         progress: TProgressTemplate[],
 *         spinner: TSpinnerTemplate[],
 *     },
 * }} TConfig
 */
export default class App {
	/**
	 * @type {TConfig}
	 */
	#config;
	/**
	 * @type {null|number}
	 */
	#pbAnimationInterval = null;
	/**
	 * @type {null|number}
	 */
	#spAnimationInterval = null;
	/**
	 * @type {number}
	 */
	#pbAnimationSpeed = 0;
	/**
	 * @type {number}
	 */
	#spAnimationSpeed = 0;
	/**
	 * @type {string[]}
	 */
	#spFrames = [];
	/**
	 * @type {number}
	 */
	#spCurrentFrameIndex = 0;

	/**
	 * @param {TConfig} config
	 */
	constructor(config) {
		this.#config = config;

		this.#initPanel('pb', this.#config.templates.progress, () => this.#refreshProgressAnimation());
		this.#initPanel('sp', this.#config.templates.spinner, () => this.#refreshSpinnerAnimation());
		// TODO pass panel animator as argument

		this.#initRanges();
	}

	/**
	 * @param {TPanelKey} panelKey
	 * @param {TBaseTemplate[]} templates
	 * @param {Function} animator
	 * @return {void}
	 */
	#initPanel(panelKey, templates, animator) {
		this.#setUpTemplateSelector(panelKey, templates);
		this.#setUpPreviewFontSelector(panelKey);

		document.getElementById(`${panelKey}-container`).addEventListener('input', (ev) => {
			if (!ev.target.matches('input, select, textarea')) {
				return;
			}

			if (ev.target.id === `${panelKey}-template`) {
				this.#loadTemplate(panelKey, templates[ev.target.selectedIndex - 1]);
			}

			if (ev.target.id.startsWith(`${panelKey}-editor-`)) {
				this.#clearTemplate(panelKey);

				const enabler = document.getElementById(`${ev.target.id}Enabled`);
				if (enabler !== null) {
					enabler.checked = true;
				}
			}

			this.#refreshPreview(panelKey, animator);
			this.#refreshShareLink(panelKey);
		});
	}

	#initRanges() {
		document.querySelectorAll('input[type="range"]').forEach(range => {
			const input = document.createElement('input');
			input.id = range.id + 'Value';
			input.type = 'number';
			input.min = range.min;
			input.max = range.max;
			input.step = range.step;
			input.value = range.value;
			input.style.marginLeft = '1ch';
			input.style.background = 'none';
			input.ariaHidden = 'true';
			range.after(input);
			range.addEventListener('input', () => {
				input.value = range.value;
			});
			input.addEventListener('input', () => {
				range.value = input.value;
				range.dispatchEvent(new Event('input', {bubbles: true}));
			});
		});
	}

	/**
	 * @param {TPanelKey} panelKey
	 * @param {TBaseTemplate[]} templates
	 * @return {void}
	 */
	#setUpTemplateSelector(panelKey, templates) {
		/** @type HTMLSelectElement */
		const templateSelect = document.getElementById(`${panelKey}-template`);
		templates.forEach(template => {
			const option = document.createElement('option');
			option.textContent = template.name;
			templateSelect.appendChild(option);
		});
	}

	/**
	 * @param {TPanelKey} panelKey
	 * @return {void}
	 */
	#setUpPreviewFontSelector(panelKey) {
		/** @type HTMLSelectElement */
		const fontSelect = document.getElementById(`${panelKey}-preview-font`);
		Object.entries(this.#config.fonts).forEach(([fontName, fontLabel]) => {
			const option = document.createElement('option');
			option.value = fontName;
			option.textContent = fontLabel || fontName;
			fontSelect.appendChild(option);
		});
	}

	/**
	 * @param {TPanelKey} panelKey
	 * @param {TBaseTemplate} template
	 * @return {void}
	 */
	#loadTemplate(panelKey, template) {
		const contrib = document.getElementById(`${panelKey}-contrib`);
		const byStr = contrib.dataset.by.split('{link}');
		contrib.innerText = byStr[0];
		template.authors.forEach(handle => {
			const link = document.createElement('a');
			link.textContent = '@' + handle;
			link.href = 'https://github.com/' + handle;
			link.target = '_blank';
			link.rel = "noopener noreferrer";
			contrib.appendChild(link);
		});
		contrib.appendChild(document.createTextNode(byStr[1]));

		Object.entries(template).forEach(([key, value]) => {
			if (['name', 'authors'].includes(key)) {
				return;
			}

			if (Array.isArray(value)) {
				value = JSON.stringify(value);
			}

			document.getElementById(`${panelKey}-editor-${key}`).value = value;
		});
	}

	/**
	 * @param {TPanelKey} panelKey
	 * @return {void}
	 */
	#clearTemplate(panelKey) {
		document.getElementById(`${panelKey}-contrib`).innerHTML = '';
		document.getElementById(`${panelKey}-template`).value = undefined;
	}

	/**
	 * @param {TPanelKey} panelKey
	 * @return {void}
	 */
	#refreshShareLink(panelKey) {
		const shareLink = document.getElementById(`${panelKey}-share-link`);
		const templateSelect = document.getElementById(`${panelKey}-template`);
		if (templateSelect.selectedIndex > 0) {
			shareLink.removeAttribute('href');
			return;
		}

		const data = {};
		const editorFieldIdPrefix = `${panelKey}-editor-`;
		document.getElementById(`${panelKey}-container`)
			.querySelectorAll('input, select, textarea')
			.forEach(field => {
				if (!field.id.startsWith(editorFieldIdPrefix)) {
					return;
				}
				if (field.id.endsWith('Enabled')) {
					return;
				}
				const enabler = document.getElementById(`${field.id}Enabled`);
				if (enabler !== null && !enabler.checked) {
					return;
				}

				data[field.id.substring(editorFieldIdPrefix.length)] = field.value
			});

		const projectUrl = 'https://github.com/uuf6429/console-progress-designer';
		const title = shareLink.dataset.title;
		const body = shareLink.dataset.body.replace('{config}', JSON.stringify(data, null, 2));
		shareLink.href = `${projectUrl}/issues/new?title=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}`;
	}

	/**
	 * @param {TPanelKey} panelKey
	 * @param {Function} animator
	 * @return {void}
	 */
	#refreshPreview(panelKey, animator) {
		/** @type HTMLDivElement */
		const preview = document.getElementById(`${panelKey}-preview`);

		preview.style.setProperty('--preview-font', document.getElementById(`${panelKey}-preview-font`).value);
		preview.style.setProperty('--preview-fg', document.getElementById(`${panelKey}-preview-fg`).value);
		preview.style.setProperty('--preview-bg', document.getElementById(`${panelKey}-preview-bg`).value);

		animator();
	}

	#redrawProgressBar(preview, barProgressRange, width) {
		const progress = Number(barProgressRange.value);
		const filled = Math.round(width * (progress / 100));
		const empty = width - filled;

		preview.textContent = '';

		const fragments = document.createDocumentFragment();

		const addSpan = (className) => {
			const span = document.createElement('span');
			span.className = className;
			fragments.appendChild(span);
		};

		addSpan('pb-l');
		for (let i = 0; i < filled; i++) {
			const isLastProgressChar = (i === filled - 1);
			addSpan(isLastProgressChar ? 'pb-rp' : 'pb-p');
		}
		for (let i = 0; i < empty; i++) {
			addSpan('pb-e');
		}
		addSpan('pb-r');

		preview.appendChild(fragments);
	}

	#refreshProgressAnimation() {
		const preview = document.getElementById('pb-preview');
		[
			['l', 'pb-editor-leftChar'],
			['p', 'pb-editor-progressChar'],
			['rp', 'pb-editor-rightProgressChar', 'pb-editor-progressChar'],
			['e', 'pb-editor-emptyChar'],
			['r', 'pb-editor-rightChar'],
		].forEach(([prefix, baseInputId, charFallbackId = null]) => {
			let char = document.getElementById(baseInputId).value;
			let inputId = baseInputId;
			if (char === '' && charFallbackId !== null) {
				char = document.getElementById(charFallbackId).value;
				inputId = charFallbackId;
			}

			preview.style.setProperty(`--pb-${prefix}-c`, JSON.stringify(char || ' '));
			preview.style.setProperty(
				`--pb-${prefix}-fg`,
				document.getElementById(inputId + 'FgEnabled').checked
					? document.getElementById(inputId + 'Fg').value
					: 'inherit'
			);
			preview.style.setProperty(
				`--pb-${prefix}-bg`,
				document.getElementById(inputId + 'BgEnabled').checked
					? document.getElementById(inputId + 'Bg').value
					: 'inherit'
			);
		});

		const barProgressRange = document.getElementById('pb-preview-barProgress');
		const barWidthInput = document.getElementById('pb-preview-barWidth');

		this.#redrawProgressBar(preview, barProgressRange, Number(barWidthInput.value));

		const animationSpeed = parseInt(document.getElementById('pb-preview-animationSpeed').value) || 0;
		if (this.#pbAnimationSpeed === animationSpeed) {
			return;
		}

		this.#pbAnimationSpeed = animationSpeed;
		if (this.#pbAnimationInterval !== null) {
			clearInterval(this.#pbAnimationInterval);
			this.#pbAnimationInterval = null;
		}
		if (animationSpeed > 0) {
			const barProgressInput = document.getElementById('pb-preview-barProgressValue');
			this.#pbAnimationInterval = setInterval(() => {
				barProgressRange.value = barProgressInput.value = ((Number(barProgressRange.value) + 1) % 101).toString();
				this.#redrawProgressBar(preview, barProgressRange, Number(barWidthInput.value));
			}, 1000 / animationSpeed);
		}
	}

	#redrawSpinner(preview) {
		if (this.#spFrames.length === 0) {
			preview.textContent = '';
			return;
		}

		preview.textContent = this.#spFrames[this.#spCurrentFrameIndex % this.#spFrames.length];
	}

	#refreshSpinnerAnimation() {
		const preview = document.getElementById('sp-preview');

		const getValues = (id) => {
			const input = document.getElementById(id);
			if (!input) return [];
			const value = input.value.trim();
			if (!value) return [];
			if (value.startsWith('[') && value.endsWith(']')) {
				try {
					return JSON.parse(value);
				} catch (e) {
					// Fallback
				}
			}
			return value.split(',').map(v => v.trim());
		};

		this.#spFrames = getValues('sp-editor-frames');

		this.#redrawSpinner(preview);

		const animationSpeed = parseInt(document.getElementById('sp-preview-animationSpeed').value) || 0;
		if (this.#spAnimationSpeed === animationSpeed) {
			return;
		}

		this.#spAnimationSpeed = animationSpeed;
		if (this.#spAnimationInterval !== null) {
			clearInterval(this.#spAnimationInterval);
			this.#spAnimationInterval = null;
		}
		if (animationSpeed > 0) {
			this.#spAnimationInterval = setInterval(() => {
				this.#spCurrentFrameIndex++;
				this.#redrawSpinner(preview);
			}, 1000 / animationSpeed);
		}
	}
}
